/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, CheckCircle, Droplets, Scale, MapPin, Sun, Wind, Thermometer, ChevronLeft, ChevronRight, Info } from 'lucide-react';

// --- HISTORY TIMELINE ---
export const Timeline: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const events = [
    { year: "3000-2000 BC", title: "Ancient Trade", desc: "Assyrians and Babylonians trade in pepper via the Malabar Coast." },
    { year: "77 BC", title: "Roman Luxury", desc: "Pliny the Elder records the price of pepper in Rome in 'Natural History'.", highlight: true },
    { year: "1st Century AD", title: "South vs North", desc: "Pliny reports black pepper from South India (Malabar) and long pepper from North India." },
    { year: "851 AD", title: "Arab Chronicles", desc: "Merchant Sulaiman visits Kerala coast, recording pepper cultivation." },
    { year: "1498", title: "Vasco da Gama", desc: "Portuguese explorer lands near Calicut, seeking 'Christians and Spices'.", highlight: true },
    { year: "1598", title: "Linschotten", desc: "Writes that 'much pepper' is found along the whole coast of Malabar." },
    { year: "1600", title: "East India Co.", desc: "Establishment of the British East India Company for spice trading." },
    { year: "1800", title: "British Supremacy", desc: "British become supreme power in pepper trade in Malabar." }
  ];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { current } = scrollContainerRef;
      const scrollAmount = 340; // Card width + gap
      current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div className="relative group px-0 md:px-12">
       {/* Navigation Buttons - Visible on large screens or hover */}
      <button 
        onClick={() => scroll('left')}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-spice-gold text-pepper-black rounded-full shadow-lg hover:scale-110 transition-all opacity-50 group-hover:opacity-100"
        aria-label="Scroll left"
      >
        <ChevronLeft size={24} />
      </button>

      <button 
        onClick={() => scroll('right')}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-spice-gold text-pepper-black rounded-full shadow-lg hover:scale-110 transition-all opacity-50 group-hover:opacity-100"
        aria-label="Scroll right"
      >
        <ChevronRight size={24} />
      </button>

      <div 
        ref={scrollContainerRef}
        className="w-full overflow-x-auto hide-scrollbar pb-8 pt-4 cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        style={{ scrollBehavior: isDragging ? 'auto' : 'smooth' }}
      >
        <div className="flex gap-8 px-4 w-max">
            {events.map((event, i) => (
            <div key={i} className={`relative flex-shrink-0 w-80 p-8 rounded-xl border transition-all duration-300 hover:-translate-y-2 ${event.highlight ? 'bg-spice-gold text-pepper-black border-spice-gold shadow-[0_0_30px_rgba(197,160,89,0.3)]' : 'bg-stone-900 text-stone-300 border-stone-800'}`}>
                <div className="text-xs font-bold tracking-widest uppercase mb-4 opacity-70 flex items-center gap-2">
                    <Clock size={12}/> {event.year}
                </div>
                <h3 className={`font-serif text-2xl mb-4 ${event.highlight ? 'text-pepper-black' : 'text-white'}`}>{event.title}</h3>
                <p className="text-sm leading-relaxed opacity-90">{event.desc}</p>
                {/* Connector Line decoration */}
                <div className="absolute top-1/2 -right-8 w-8 h-[1px] bg-stone-700"></div>
            </div>
            ))}
        </div>
      </div>
    </div>
  );
};

// --- INTERACTIVE MAP VISUALIZATION ---
export const MapView: React.FC = () => {
    const [activeState, setActiveState] = useState<string | null>("Kerala");
    const [hoveredDistrict, setHoveredDistrict] = useState<{name: string, x: number, y: number} | null>(null);

    // Precise abstract coordinates for South India (0-100 grid)
    // Roughly mapped: Top-Left (15, 10) to Bottom-Right (85, 100)
    // Tip of India (Kanyakumari) approx at (50, 95)
    
    const regions = [
        { 
            name: "Kerala", 
            color: "text-emerald-700",
            fill: "fill-emerald-600",
            bg: "bg-emerald-50",
            border: "border-emerald-200",
            description: "The primary origin. Malabar pepper originated as chance seedlings here.",
            districts: [
                { name: "Kasaragod", x: 30, y: 55 },
                { name: "Kannur", x: 32, y: 58 },
                { name: "Wayanad", x: 36, y: 60 },
                { name: "Kozhikode", x: 34, y: 63 },
                { name: "Malappuram", x: 36, y: 66 },
                { name: "Palakkad", x: 40, y: 68 },
                { name: "Thrissur", x: 38, y: 72 },
                { name: "Ernakulam", x: 38, y: 76 },
                { name: "Idukki", x: 44, y: 78 },
                { name: "Kottayam", x: 40, y: 80 },
                { name: "Alapuzha", x: 38, y: 82 },
                { name: "Pathanamthitta", x: 42, y: 84 },
                { name: "Kollam", x: 40, y: 88 },
                { name: "Thiruvananthapuram", x: 42, y: 92 },
            ]
        },
        { 
            name: "Karnataka", 
            color: "text-amber-700",
            fill: "fill-amber-600",
            bg: "bg-amber-50",
            border: "border-amber-200",
            description: "Produces high grade pepper in the Western Ghats region.",
            districts: [
                { name: "Uttara Kannada", x: 25, y: 35 },
                { name: "Dharwad", x: 30, y: 32 },
                { name: "Davangere", x: 40, y: 35 },
                { name: "Shimoga", x: 32, y: 40 },
                { name: "Udupi", x: 28, y: 45 },
                { name: "Chickmagalur", x: 35, y: 45 },
                { name: "Dakshina Kannada", x: 30, y: 50 },
                { name: "Hassan", x: 38, y: 50 },
                { name: "Tumkur", x: 45, y: 45 },
                { name: "Bangalore R", x: 50, y: 48 },
                { name: "Mandya", x: 45, y: 52 },
                { name: "Mysore", x: 42, y: 55 },
                { name: "Kodagu", x: 35, y: 55 },
                { name: "Chamarajanagar", x: 45, y: 58 },
            ]
        },
        { 
            name: "Tamil Nadu", 
            color: "text-rose-700",
            fill: "fill-rose-600",
            bg: "bg-rose-50",
            border: "border-rose-200",
            description: "Cultivation in the hilly regions bordering the Western Ghats.",
            districts: [
                { name: "Krishnagiri", x: 55, y: 40 },
                { name: "Vellore", x: 60, y: 38 },
                { name: "Salem", x: 52, y: 50 },
                { name: "Namakkal", x: 52, y: 55 },
                { name: "Erode", x: 48, y: 55 },
                { name: "Nilgiris", x: 40, y: 60 },
                { name: "Coimbatore", x: 45, y: 62 },
                { name: "Dindigul", x: 50, y: 70 },
                { name: "Theni", x: 48, y: 75 },
                { name: "Thanjavur", x: 60, y: 65 },
                { name: "Pudukottai", x: 60, y: 70 },
                { name: "Tirunelveli", x: 48, y: 88 },
                { name: "Kanniyakumari", x: 50, y: 95 },
            ]
        }
    ];

    return (
        <div className="bg-white p-6 md:p-8 rounded-xl border border-stone-200 shadow-sm flex flex-col md:flex-row gap-8">
            {/* Interactive Map Surface */}
            <div className="relative w-full md:w-1/2 aspect-[3/4] bg-[#F5F8FA] rounded-xl border border-stone-100 overflow-hidden group select-none">
                
                {/* SVG Map */}
                <svg viewBox="0 0 100 110" className="w-full h-full drop-shadow-md">
                    <defs>
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>

                    {/* Abstract Landmass - Stylized South India */}
                    <path 
                        d="M 15 20 Q 25 20 30 25 T 40 30 T 55 25 T 70 30 L 75 60 Q 70 80 50 98 Q 30 80 25 60 T 15 30 Z" 
                        fill="#E2E8F0" 
                        stroke="#CBD5E1"
                        strokeWidth="0.5"
                    />
                    
                    {/* Render Regions */}
                    {regions.map((region) => (
                        <g key={region.name} className={`transition-opacity duration-500 ${activeState && activeState !== region.name ? 'opacity-30' : 'opacity-100'}`}>
                            {region.districts.map((district, i) => (
                                <g key={district.name}>
                                    {/* Ripple Effect for active state */}
                                    {activeState === region.name && (
                                        <circle 
                                            cx={district.x} 
                                            cy={district.y} 
                                            r="4" 
                                            className={`${region.color} opacity-20 origin-center animate-ping`}
                                            style={{ animationDelay: `${i * 100}ms`, animationDuration: '3s' }}
                                        />
                                    )}
                                    
                                    {/* The Dot */}
                                    <circle 
                                        cx={district.x} 
                                        cy={district.y} 
                                        r={hoveredDistrict?.name === district.name ? 2 : 1.2}
                                        className={`cursor-pointer transition-all duration-300 ${region.fill}`}
                                        stroke="white"
                                        strokeWidth="0.3"
                                        onMouseEnter={() => setHoveredDistrict(district)}
                                        onMouseLeave={() => setHoveredDistrict(null)}
                                    />

                                    {/* Connecting lines for aesthetics (Constellation look) */}
                                    {i > 0 && activeState === region.name && (
                                        <line 
                                            x1={region.districts[i-1].x} 
                                            y1={region.districts[i-1].y} 
                                            x2={district.x} 
                                            y2={district.y} 
                                            stroke={region.fill === "fill-emerald-600" ? "#059669" : region.fill === "fill-amber-600" ? "#D97706" : "#E11D48"}
                                            strokeWidth="0.2"
                                            strokeOpacity="0.4"
                                        />
                                    )}
                                </g>
                            ))}
                        </g>
                    ))}
                </svg>

                {/* Tooltip Overlay */}
                <AnimatePresence>
                    {hoveredDistrict && (
                        <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="absolute z-20 pointer-events-none bg-pepper-black text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap"
                            style={{ 
                                left: `${hoveredDistrict.x}%`, 
                                top: `${hoveredDistrict.y - 4}%`,
                                transform: 'translate(-50%, -100%)' 
                            }}
                        >
                            {hoveredDistrict.name}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Legend/Key */}
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg border border-stone-200 text-[10px] shadow-sm">
                    <div className="font-bold text-stone-600 mb-2 uppercase tracking-wider">Production Zones</div>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-600"></span> Kerala
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-600"></span> Karnataka
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-rose-600"></span> Tamil Nadu
                        </div>
                    </div>
                </div>
            </div>

            {/* List/Controls Side */}
            <div className="w-full md:w-1/2 flex flex-col justify-center">
                <div className="mb-6">
                    <h3 className="font-serif text-2xl text-pepper-black mb-2">Production Districts</h3>
                    <p className="text-stone-500 text-sm">Select a state to visualize the specific geographical indication zones as defined in the application registry.</p>
                </div>

                <div className="space-y-3">
                    {regions.map((region) => (
                        <div 
                            key={region.name}
                            className={`p-4 rounded-lg border transition-all duration-300 cursor-pointer ${activeState === region.name ? `${region.bg} ${region.border} shadow-sm` : 'bg-white border-stone-100 hover:border-stone-300'}`}
                            onClick={() => setActiveState(activeState === region.name ? null : region.name)}
                            onMouseEnter={() => setActiveState(region.name)}
                        >
                            <div className="flex justify-between items-center mb-2 gap-4">
                                <h4 className={`font-bold ${activeState === region.name ? region.color : 'text-stone-700'} flex items-center gap-2 min-w-0`}>
                                    <MapPin size={16} className={`flex-shrink-0 ${activeState === region.name ? region.color : 'text-stone-400'}`} />
                                    <span className="truncate">{region.name}</span>
                                </h4>
                                <span className="text-xs font-mono text-stone-400 whitespace-nowrap flex-shrink-0">{region.districts.length} Districts</span>
                            </div>
                            
                            <motion.div 
                                initial={false}
                                animate={{ height: activeState === region.name ? 'auto' : 0, opacity: activeState === region.name ? 1 : 0 }}
                                className="overflow-hidden"
                            >
                                <p className="text-xs text-stone-500 mb-3 italic">{region.description}</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {region.districts.map(d => (
                                        <span 
                                            key={d.name} 
                                            className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${hoveredDistrict?.name === d.name ? 'bg-pepper-black text-white border-pepper-black' : 'bg-white text-stone-600 border-stone-200'}`}
                                            onMouseEnter={() => setHoveredDistrict(d)}
                                            onMouseLeave={() => setHoveredDistrict(null)}
                                        >
                                            {d.name}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

// --- PRODUCTION CYCLE ---
export const ProductionCycle: React.FC = () => {
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
                { step: "1", icon: <Sun size={24} />, title: "Harvest", desc: "Berries harvested when one or two turn red." },
                { step: "2", icon: <Thermometer size={24} />, title: "Blanching", desc: "Briefly in hot water (1 min) to clean & prep." },
                { step: "3", icon: <Wind size={24} />, title: "Drying", desc: "Sun dried for days. Fruit shrinks & darkens." },
                { step: "4", icon: <CheckCircle size={24} />, title: "Garbling", desc: "Grading and removing extraneous matter." },
            ].map((item, i) => (
                <div key={i} className="bg-white p-6 rounded-xl border border-stone-200 flex flex-col items-center text-center group hover:border-spice-gold transition-colors">
                    <div className="w-12 h-12 rounded-full bg-[#F9F8F4] flex items-center justify-center text-spice-brown mb-4 group-hover:bg-spice-gold group-hover:text-white transition-colors">
                        {item.icon}
                    </div>
                    <h4 className="font-serif text-lg text-pepper-black mb-2">{item.title}</h4>
                    <p className="text-xs text-stone-500 leading-relaxed">{item.desc}</p>
                </div>
            ))}
        </div>
    )
}

// --- GRADING SPECIFICATIONS ---
export const GradingSpecs: React.FC = () => {
    const [grade, setGrade] = useState<'MG1' | 'MG2'>('MG1');
    
    // MG Grade 1 Data
    const mg1 = { extraneous: 0.5, light: 2.0, moisture: 11 };
    // MG Grade 2 Data
    const mg2 = { extraneous: 0.5, light: 5.0, moisture: 11 };

    const current = grade === 'MG1' ? mg1 : mg2;

    return (
        <div className="p-8 bg-white rounded-xl border border-stone-200 shadow-lg">
            <div className="flex justify-between items-center mb-8">
                <h3 className="font-serif text-2xl text-pepper-black">Grade Specifications</h3>
                <div className="flex bg-stone-100 p-1 rounded-lg">
                    <button 
                        onClick={() => setGrade('MG1')}
                        className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${grade === 'MG1' ? 'bg-white shadow text-pepper-black' : 'text-stone-400 hover:text-stone-600'}`}
                    >
                        MG Grade 1
                    </button>
                    <button 
                        onClick={() => setGrade('MG2')}
                        className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${grade === 'MG2' ? 'bg-white shadow text-pepper-black' : 'text-stone-400 hover:text-stone-600'}`}
                    >
                        MG Grade 2
                    </button>
                </div>
            </div>

            <div className="space-y-8">
                {/* Extraneous Matter */}
                <div className="relative">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm font-bold text-stone-600 flex items-center gap-2">
                             <Scale size={16} /> Extraneous Matter
                        </span>
                        <span className="font-mono text-spice-brown font-bold">{current.extraneous}% Max</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <motion.div 
                            className="h-full bg-stone-800"
                            initial={{ width: 0 }}
                            animate={{ width: `${(current.extraneous / 5) * 100}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                    <p className="text-xs text-stone-400 mt-2">Dust, chaff, pickings, foreign matter.</p>
                </div>

                {/* Light Berries */}
                <div className="relative">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm font-bold text-stone-600 flex items-center gap-2">
                             <Sun size={16} /> Light Berries
                        </span>
                        <span className="font-mono text-spice-brown font-bold">{current.light}% Max</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <motion.div 
                            className="h-full bg-spice-gold"
                            initial={{ width: 0 }}
                            animate={{ width: `${(current.light / 10) * 100}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                    <p className="text-xs text-stone-400 mt-2">Tested by flotation method in alcohol.</p>
                </div>

                {/* Moisture */}
                <div className="relative">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm font-bold text-stone-600 flex items-center gap-2">
                             <Droplets size={16} /> Moisture Content
                        </span>
                        <span className="font-mono text-spice-brown font-bold">{current.moisture}% Max</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <motion.div 
                            className="h-full bg-blue-400"
                            initial={{ width: 0 }}
                            animate={{ width: `${(current.moisture / 20) * 100}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                    <p className="text-xs text-stone-400 mt-2">Tolerance of 0.5% allowed during monsoon (May 15 - Sept 30).</p>
                </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-stone-100 text-center">
                <p className="font-serif italic text-stone-600 text-sm">
                    "Shall be the dried mature berries of Piper nigrum grown in South India, garbled, dark brown to dark black in colour, nearly globular, with a wrinkled surface."
                </p>
            </div>
        </div>
    )
}